import { CARRIER_NAMES } from '@shared';
import { formatCurrency } from '@shared/formatCurrency';

export type ProductDetailEntry = {
  label: string;
  value: string | number;
  testId?: string;
};

export type ProductDetailSection = {
  title: string;
  items: ProductDetailEntry[];
};

export interface ProductDetailsQuote {
  carrierName: string;
  productName: string;
  policyType: string;
  premiumAmount: number;
  primaryTobaccoStatus: string;
  secondaryTobaccoStatus?: string;
  riskClass?: string;
  ltcBenefitMonthlyIndividual: number;
  ltcBenefitAnnualIndividual?: number;
  ltcBenefitTotal: number;
  ltcBenefitDurationMonths?: number;
  deathBenefitMinimumGuaranteedIndividual?: number;
  deathBenefitInitial?: number;
  aobDuration?: string;
  cobDuration?: string;
  aobInflation?: string;
  cobInflation?: string;
  inflationBenefitOption?: string;
  inflationDuration?: string;
  cobPaymentOption?: string;
  notes?: string;
  returnOfPremiumPercent?: string;
  maxMonthlyLTCBenefitReimbursement?: number;
  maxMonthlyLTCBenefitIndemnity?: number;
  hasMec?: boolean;
  claimAge?: number;
  coverageRatio?: number;
  minCoverageRatio?: number;
  guaranteedAccumulationValue?: number;
}

function formatRiskClass(
  carrierName: string,
  primaryTobaccoStatus: string,
  secondaryTobaccoStatus?: string,
  riskClass?: string,
): string {
  const isNationwide = carrierName === CARRIER_NAMES.NATIONWIDE;
  const isEquiTrust = carrierName === CARRIER_NAMES.EQUITRUST;

  if (isEquiTrust && riskClass) {
    return riskClass.charAt(0).toUpperCase() + riskClass.slice(1).toLowerCase();
  }

  if (!secondaryTobaccoStatus) {
    return isNationwide ? `Standard ${primaryTobaccoStatus}` : primaryTobaccoStatus;
  }

  const formatJointStatus = (status: string) => (isNationwide ? `Preferred ${status}` : status);
  const primary = formatJointStatus(primaryTobaccoStatus);
  const secondary = formatJointStatus(secondaryTobaccoStatus);
  return `${primary} + ${secondary}`;
}

export function getProductDetailsSections(quote: ProductDetailsQuote): ProductDetailSection[] {
  const {
    carrierName,
    policyType,
    premiumAmount,
    primaryTobaccoStatus,
    secondaryTobaccoStatus,
    riskClass,
    ltcBenefitMonthlyIndividual,
    ltcBenefitAnnualIndividual,
    ltcBenefitTotal,
    ltcBenefitDurationMonths,
    deathBenefitMinimumGuaranteedIndividual,
    deathBenefitInitial,
    aobDuration,
    cobDuration,
    aobInflation,
    cobInflation,
    inflationBenefitOption,
    inflationDuration,
    cobPaymentOption,
    notes,
    returnOfPremiumPercent,
    maxMonthlyLTCBenefitReimbursement,
    maxMonthlyLTCBenefitIndemnity,
    hasMec,
    claimAge,
    coverageRatio,
    minCoverageRatio,
    guaranteedAccumulationValue,
  } = quote;

  const isLincoln = carrierName === CARRIER_NAMES.LINCOLN_FINANCIAL;
  const isNationwide = carrierName === CARRIER_NAMES.NATIONWIDE;
  const isSecurian = carrierName === CARRIER_NAMES.SECURIAN_FINANCIAL;
  const isEquiTrust = carrierName === CARRIER_NAMES.EQUITRUST;
  const isLincolnWithMaxBenefits =
    isLincoln && (maxMonthlyLTCBenefitReimbursement || maxMonthlyLTCBenefitIndemnity);
  const is10PayQuote = cobPaymentOption === '10-Pay';
  const isJointLife = policyType === 'Joint Life';

  const getLtcBenefitPeriod = (): string | null => {
    if (!ltcBenefitDurationMonths) return null;

    if (isLincoln || isNationwide || isSecurian) {
      if (isJointLife) {
        return `${ltcBenefitDurationMonths} months`;
      } else {
        const years = ltcBenefitDurationMonths / 12;
        return `${years} year${years === 1 ? '' : 's'}`;
      }
    }
    return null;
  };

  const getInflationOptionText = (): string => {
    const parts = [];
    if (aobInflation) parts.push(`${aobInflation} AOB`);
    if (cobInflation) parts.push(`${cobInflation} COB`);
    if (inflationBenefitOption) parts.push(inflationBenefitOption);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const benefitLimitItems: ProductDetailEntry[] = [
    ...(!isLincolnWithMaxBenefits
      ? [
          {
            label: 'LTC Coverage',
            value: `${formatCurrency(Math.ceil(ltcBenefitMonthlyIndividual))}/month ${isJointLife ? 'per insured' : ''}`,
          },
        ]
      : []),
    ...(isLincolnWithMaxBenefits && maxMonthlyLTCBenefitReimbursement
      ? [
          {
            label: 'Max Monthly (Reimbursement)',
            value: `${formatCurrency(Math.ceil(maxMonthlyLTCBenefitReimbursement))}/month`,
          },
        ]
      : []),
    ...(isLincolnWithMaxBenefits && maxMonthlyLTCBenefitIndemnity
      ? [
          {
            label: 'Max Monthly (Indemnity)',
            value: `${formatCurrency(Math.ceil(maxMonthlyLTCBenefitIndemnity))}/month`,
          },
        ]
      : []),
    ...(ltcBenefitAnnualIndividual
      ? [
          {
            label: 'Annual',
            value: `${formatCurrency(Math.ceil(ltcBenefitAnnualIndividual))}${isJointLife ? ' per insured' : ''}`,
          },
        ]
      : []),
    {
      label: 'Total LTC Benefit',
      value: ltcBenefitTotal === 0 ? 'Unlimited' : formatCurrency(Math.ceil(ltcBenefitTotal)),
    },
    ...(getLtcBenefitPeriod()
      ? [
          {
            label: 'LTC Benefit Period',
            value: getLtcBenefitPeriod()!,
            testId: 'quote-detail-benefit-duration',
          },
        ]
      : []),
    ...(isEquiTrust && coverageRatio
      ? [
          {
            label: 'Max Coverage Ratio',
            value: `${coverageRatio}%`,
            testId: 'quote-detail-coverage-ratio',
          },
        ]
      : []),
    ...(isEquiTrust && guaranteedAccumulationValue
      ? [
          {
            label: 'Guaranteed Accumulation Value',
            value: `${formatCurrency(Math.ceil(guaranteedAccumulationValue))} (at ${minCoverageRatio ?? 100}% coverage)`,
            testId: 'quote-detail-gav',
          },
        ]
      : []),
    ...(deathBenefitMinimumGuaranteedIndividual
      ? [
          {
            label: 'Min Guaranteed Death Benefit',
            value: formatCurrency(Math.ceil(deathBenefitMinimumGuaranteedIndividual)),
          },
        ]
      : []),
    ...(deathBenefitInitial
      ? [
          {
            label: 'Death Benefit',
            value: formatCurrency(Math.ceil(deathBenefitInitial)),
          },
        ]
      : []),
  ];

  const featureItems: ProductDetailEntry[] = [
    ...(isEquiTrust && claimAge
      ? [
          {
            label: 'Benefits Available Starting',
            value: `Age ${claimAge}`,
            testId: 'quote-detail-claim-age',
          },
        ]
      : []),
    ...(aobDuration && cobDuration
      ? [
          {
            label: 'Benefit Period',
            value: `${aobDuration} AOB/${cobDuration} COB`,
            testId: 'quote-detail-benefit-duration',
          },
        ]
      : []),
    ...(inflationBenefitOption || aobInflation || cobInflation
      ? [
          {
            label: 'Inflation Option',
            value: getInflationOptionText(),
            testId: 'quote-detail-inflation-protection',
          },
        ]
      : []),
    ...(inflationDuration ? [{ label: 'Inflation Duration', value: inflationDuration }] : []),
    ...(isLincoln
      ? [
          {
            label: 'Policy Type',
            value: policyType,
          },
        ]
      : [
          {
            label: 'Class',
            value: formatRiskClass(
              carrierName,
              primaryTobaccoStatus,
              secondaryTobaccoStatus,
              riskClass,
            ),
          },
        ]),
    ...(hasMec && !(isSecurian && is10PayQuote) ? [{ label: 'MEC', value: 'Yes' }] : []),
    ...(notes ? [{ label: '', value: notes }] : []),
  ];

  const premiumItems: ProductDetailEntry[] = [
    {
      label: is10PayQuote ? 'Annual Planned Premium' : 'Total Premium',
      value: formatCurrency(Math.round(premiumAmount)),
      testId: 'quote-detail-premium',
    },
    ...(isLincoln && returnOfPremiumPercent
      ? [
          {
            label: 'Return of Premium',
            value: `${returnOfPremiumPercent}%`,
          },
        ]
      : []),
  ];

  return [
    { title: 'Initial LTC Benefit Limit', items: benefitLimitItems },
    { title: 'Features and Benefits', items: featureItems },
    { title: 'Premium', items: premiumItems },
  ];
}

export interface TraditionalLTCDetailsQuote {
  carrier: string;
  productName: string;
  primaryAge: number;
  primaryGender: string;
  maritalClass: string;
  underwritingClass: string;
  benefitPool: number;
  dailyBenefitAmount: number;
  eliminationPeriod: number;
  bioPercent: string;
  premiumFrequency: string;
  primaryModalPremium: number;
  primaryTotalYearlyPremium: number;
  dp1Rider?: boolean;
  nfoRider?: boolean;
}

function formatGender(gender: string): string {
  return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
}

function formatMaritalStatus(maritalClass: string): string {
  if (maritalClass === 'Married 1 Buy') {
    return 'Married, 1 Buyer';
  }
  if (maritalClass === 'Married 2 Buy') {
    return 'Married, 2 Buyers';
  }
  return maritalClass;
}

export function getTraditionalLTCDetailsSections(
  quote: TraditionalLTCDetailsQuote,
): ProductDetailSection[] {
  const policyDetailsItems: ProductDetailEntry[] = [
    { label: 'Age', value: quote.primaryAge, testId: 'quote-detail-age' },
    { label: 'Gender', value: formatGender(quote.primaryGender), testId: 'quote-detail-gender' },
    {
      label: 'Marital Status',
      value: formatMaritalStatus(quote.maritalClass),
      testId: 'quote-detail-marital-status',
    },
    {
      label: 'Underwriting Class',
      value: quote.underwritingClass,
      testId: 'quote-detail-underwriting-class',
    },
  ];

  const benefitsItems: ProductDetailEntry[] = [
    {
      label: 'Benefit Pool',
      value: `$${quote.benefitPool.toLocaleString()}`,
      testId: 'quote-detail-benefit-pool',
    },
    {
      label: 'Daily Benefit',
      value: `$${quote.dailyBenefitAmount}`,
      testId: 'quote-detail-daily-benefit',
    },
    {
      label: 'Elimination Period',
      value: `${quote.eliminationPeriod} days`,
      testId: 'quote-detail-elimination-period',
    },
    {
      label: 'Benefit Inflation Option',
      value: quote.bioPercent === 'No BIO' ? 'None' : quote.bioPercent.replace(' BIO', ''),
    },
  ];

  const premiumItems: ProductDetailEntry[] = [
    { label: 'Payment Frequency', value: quote.premiumFrequency },
    {
      label: 'Modal Premium',
      value: `$${quote.primaryModalPremium.toLocaleString()}`,
      testId: 'quote-detail-premium',
    },
    { label: 'Yearly Premium', value: `$${quote.primaryTotalYearlyPremium.toLocaleString()}` },
    ...(quote.dp1Rider ? [{ label: '', value: '✓ DP1 Rider' }] : []),
    ...(quote.nfoRider ? [{ label: '', value: '✓ NFO Rider' }] : []),
  ];

  return [
    { title: 'Policy Details', items: policyDetailsItems },
    { title: 'Benefits', items: benefitsItems },
    { title: 'Premiums', items: premiumItems },
  ];
}
